import { Component, OnInit, Input, Output, EventEmitter, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { TerminalService, Terminal, TerminalHealth } from '../../services/terminal.service';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { UsersService } from '../../services/users.service';
import { Subscription } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

@Component({
	selector: 'app-terminal-device-selector',
	templateUrl: './terminal-device-selector.component.html',
	styleUrls: ['./terminal-device-selector.component.scss']
})
export class TerminalDeviceSelectorComponent implements OnInit, OnDestroy, OnChanges {
  @Input() locationId: string;
  @Input() required = true;
  @Input() selectedTerminalId: number | string | null = null;
  @Output() terminalSelected = new EventEmitter<Terminal>();
  @Output() terminalDeselected = new EventEmitter<void>();

  terminals: Terminal[] = [];
  selectedTerminal: Terminal | null = null;
  loading = false;
  discovering = false;
  healthSubscriptions: Map<number | string, Subscription> = new Map();
  terminalHealth: Map<number | string, TerminalHealth> = new Map();

  constructor(
    private terminalService: TerminalService,
    private globalSearchService: GlobalSearchService,
    private usersService: UsersService,
    private modalService: NgbModal
  ) {}

  ngOnInit() {
  	this.loadTerminals();
  	this.setupHealthMonitoring();
  }

  ngOnChanges(changes: SimpleChanges) {
  	if (changes.selectedTerminalId && this.terminals.length) {
  		this.applyPresetSelection();
  	}
  }

  ngOnDestroy() {
  	this.healthSubscriptions.forEach(sub => sub.unsubscribe());
  	this.terminalService.stopHealthMonitoring();
  }

  loadTerminals(forceRefresh = false) {
  	this.loading = true;
  	const userVal = this.globalSearchService.user.getValue();
  	const user = userVal?.user || userVal;
  	if (!user?.userid) {
  		this.terminals = [];
  		this.loading = false;
  		return;
  	}
  	this.usersService.getUserTerminals({ userid: user.userid }).subscribe(
  		(rows: any) => {
  			this.terminals = (rows || []).map((t: any) => this.normalizeTerminal(t));
  			this.loading = false;
  			this.applyPresetSelection();
  			this.updateHealthForAll();
  		},
  		(error) => {
  			this.globalSearchService.showNotification('Failed to load terminals', 'danger', 'bottom', 'right');
  			this.loading = false;
  		}
  	);
  }

  private applyPresetSelection() {
  	if (!this.selectedTerminalId) {
  		return;
  	}
  	const preset = this.terminals.find(t => t.terminal_id === this.selectedTerminalId);
  	if (preset && this.selectedTerminal?.terminal_id !== preset.terminal_id) {
  		this.selectedTerminal = preset;
  		this.terminalSelected.emit(preset);
  	}
  }

  private normalizeTerminal(t: any): Terminal {
  	return {
  		terminal_id: t.terminal_id,
  		terminal_name: t.terminal_name || 'Unnamed Terminal',
  		terminal_type: t.terminal_type,
  		terminal_model: t.terminal_model || 'desk5000',
  		terminal_serial: t.terminal_serial || '',
  		connection_type: (t.connection_type || 'lan') as Terminal['connection_type'],
  		is_active: true,
  		is_available: true,
  		connection_config: typeof t.connection_config === 'string' ? JSON.parse(t.connection_config || '{}') : t.connection_config,
  		needs_registration: false
  	};
  }

  refreshTerminals() {
  	this.discovering = true;
  	this.loadTerminals(true);
  	setTimeout(() => {
  		this.discovering = false;
  	}, 1500);
  }

  selectTerminal(terminal: Terminal) {
  	if (!terminal.is_available && terminal.current_transaction_id) {
  		this.globalSearchService.showNotification('Terminal is currently processing a transaction', 'warning', 'bottom', 'right');
  		return;
  	}

  	if (terminal.needs_registration) {
  		this.showRegistrationModal(terminal);
  		return;
  	}

  	this.selectedTerminal = terminal;
  	this.terminalSelected.emit(terminal);
  }

  deselectTerminal() {
  	this.selectedTerminal = null;
  	this.terminalDeselected.emit();
  }

  showRegistrationModal(terminal: Terminal) {
  	const terminalName = prompt('Enter a name for this terminal:', terminal.terminal_name || 'Terminal ' + terminal.terminal_serial);
  	if (terminalName) {
  		const registrationData = {
  			terminal_name: terminalName,
  			terminal_model: terminal.terminal_model,
  			terminal_serial: terminal.terminal_serial,
  			connection_type: terminal.connection_type,
  			connection_config: typeof terminal.connection_config === 'string'
  				? JSON.parse(terminal.connection_config)
  				: terminal.connection_config,
  			location_id: this.locationId
  		};

  		this.terminalService.registerTerminal(registrationData).subscribe(
  			(result) => {
  				if (result.success) {
  					this.globalSearchService.showNotification('Terminal registered successfully', 'success', 'bottom', 'right');
  					this.loadTerminals(true);
  				}
  			},
  			(error) => {
  				this.globalSearchService.showNotification('Failed to register terminal', 'danger', 'bottom', 'right');
  			}
  		);
  	}
  }

  getTerminalIcon(model: string): string {
  	switch(model) {
  	case 'imove5000':
  		return 'phone_android';
  	case 'desk5000':
  		return 'desktop_windows';
  	default:
  		return 'credit_card';
  	}
  }

  getConnectionIcon(connectionType: string): string {
  	switch(connectionType) {
  	case 'usb':
  		return 'usb';
  	case 'lan':
  		return 'ethernet';
  	case 'bluetooth':
  		return 'bluetooth';
  	case 'wifi':
  		return 'wifi';
  	default:
  		return 'link';
  	}
  }

  getStatusColor(terminal: Terminal): string {
  	if (!terminal.is_active) {return 'gray';}
  	if (!terminal.is_available) {return 'orange';}
  	if (terminal.current_transaction_id) {return 'blue';}
  	return 'green';
  }

  getBatteryIcon(level: number | undefined): string {
  	if (!level) {return 'battery_unknown';}
  	if (level > 75) {return 'battery_full';}
  	if (level > 50) {return 'battery_3_bar';}
  	if (level > 25) {return 'battery_2_bar';}
  	return 'battery_1_bar';
  }

  getSignalIcon(strength: number | undefined): string {
  	if (!strength) {return 'signal_wifi_off';}
  	if (strength > 75) {return 'signal_wifi_4_bar';}
  	if (strength > 50) {return 'signal_wifi_3_bar';}
  	if (strength > 25) {return 'signal_wifi_2_bar';}
  	return 'signal_wifi_1_bar';
  }

  setupHealthMonitoring() {
  	if (this.terminalService && this.terminalService['omsService'] && this.terminalService['omsService'].socket) {
  		this.terminalService['omsService'].socket.on('terminal_health_update', (data: any) => {
  			if (data.terminal_id && data.health) {
  				this.terminalHealth.set(data.terminal_id, data.health);
  			}
  		});
  	}
  }

  updateHealthForAll() {
  	this.terminals.forEach(terminal => {
  		if (terminal.terminal_id) {
  			this.terminalService.getTerminalHealth(terminal.terminal_id as number).subscribe(
  				(health) => {
  					this.terminalHealth.set(terminal.terminal_id!, health);
  				},
  				() => {}
  			);
  		}
  	});
  }
}
